import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowUpDown, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { suggestProducts } from "../../api/product";

type SuggestItem = {
  id: number;
  ad: string;
  fiyat: number;
  paraBirimi: string;
  fotoMain?: string | null;
  fotoList: string[];
};

const DEBOUNCE = 250;

function cn(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(" ");
}

const highlight = (text: string, q: string) => {
  const query = q.trim();
  if (!query) return text;

  const i = text.toLowerCase().indexOf(query.toLowerCase());
  if (i === -1) return text;

  return (
    <>
      {text.slice(0, i)}
      <mark className="bg-transparent font-semibold text-[rgb(var(--primary))]">
        {text.slice(i, i + query.length)}
      </mark>
      {text.slice(i + query.length)}
    </>
  );
};

const formatMoney = (amount: number, currency?: string) => {
  const cur = currency || "TRY";
  return new Intl.NumberFormat("tr-TR", { style: "currency", currency: cur }).format(amount);
};

const SearchBox: React.FC<{
  initial?: string;
  className?: string;
  onResultSelect?: () => void;
  autoFocus?: boolean;
}> = ({ initial = "", className, onResultSelect, autoFocus }) => {
  const nav = useNavigate();

  const [q, setQ] = useState(initial);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<SuggestItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const boxRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const timerRef = useRef<number | null>(null);
  const seqRef = useRef(0);

  const trimmed = useMemo(() => q.trim(), [q]);

  // dış tık kapatma
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    return () => window.removeEventListener("pointerdown", onDown);
  }, []);

  // query -> suggestion
  useEffect(() => {
    if (!trimmed || trimmed.length < 2) {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      setItems([]);
      setOpen(false);
      setActiveIndex(-1);
      setLoading(false);
      return;
    }

    if (timerRef.current) window.clearTimeout(timerRef.current);

    const mySeq = ++seqRef.current;
    timerRef.current = window.setTimeout(async () => {
      try {
        setLoading(true);
        const res = await suggestProducts(trimmed);
        if (mySeq !== seqRef.current) return;

        if (res.success && Array.isArray(res.data)) {
          setItems(res.data);
          setOpen(true);
          setActiveIndex(-1);
        } else {
          setItems([]);
          setOpen(false);
          setActiveIndex(-1);
        }
      } finally {
        if (mySeq === seqRef.current) setLoading(false);
      }
    }, DEBOUNCE);
  }, [trimmed]);

  const closeAll = () => {
    setOpen(false);
    setActiveIndex(-1);
    onResultSelect?.();
  };

  const goResults = () => {
    const query = trimmed;
    nav(query ? `/products?search=${encodeURIComponent(query)}` : "/products");
    closeAll();
  };

  const goActiveItem = () => {
    if (activeIndex >= 0 && activeIndex < items.length) {
      nav(`/urun/${items[activeIndex].id}`);
      closeAll();
      return true;
    }
    return false;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!goActiveItem()) goResults();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (!goActiveItem()) goResults();
    }
  };

  const showPanel = open;

  return (
    <div ref={boxRef} className={cn("relative", className)}>
      <form onSubmit={onSubmit}>
        <div
          className={cn(
            "group flex items-center rounded-2xl border overflow-hidden",
            "bg-[rgb(var(--surface))] shadow-sm focus-within:shadow-md transition"
          )}
          style={{ borderColor: "rgb(var(--border))" }}
        >
          <div className="pl-4 pr-2 py-2.5 flex items-center gap-2 w-full">
            <Search size={16} className="opacity-60 text-[rgb(var(--text-muted))]" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ürün, marka, model ara…"
              className="w-full bg-transparent outline-none text-sm placeholder:text-slate-400"
              style={{ color: "rgb(var(--text))" }}
              aria-label="Ürün ara"
              autoFocus={autoFocus}
              onFocus={() => {
                if (trimmed.length >= 2 && items.length > 0) setOpen(true);
              }}
            />
          </div>

          <button
            type="submit"
            className={cn(
              "shrink-0 h-full px-4 py-2.5 text-sm font-semibold bg-gradient-to-r from-blue-600 to-teal-500 text-white",
              "transition active:scale-[0.99]"
            )}

            aria-label="Ara"
          >
            <span className="inline-flex items-center gap-2">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            </span>
          </button>
        </div>
      </form>

      <AnimatePresence>
        {showPanel && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ type: "tween", duration: 0.14 }}
            className={cn(
              "absolute left-0 right-0 mt-2 z-50",
              "rounded-2xl border shadow-2xl overflow-hidden",
              "bg-slate-50"
            )}
            style={{ borderColor: "rgb(var(--border))", zIndex: 9999 }}
            role="listbox"
          >
            {loading ? (
              <div className="p-4 text-sm flex items-center gap-2 text-[rgb(var(--text-muted))]">
                <Loader2 size={16} className="animate-spin" />
                Yükleniyor…
              </div>
            ) : items.length === 0 ? (
              <div className="p-4 text-sm text-[rgb(var(--text-muted))]">Sonuç bulunamadı.</div>
            ) : (
              <>
                <ul className="max-h-80 overflow-auto">
                  {items.map((p, idx) => {
                    const img = p.fotoMain ?? p.fotoList?.[0] ?? "";
                    const active = activeIndex === idx;

                    return (
                      <li key={p.id}>
                        <Link
                          to={`/urun/${p.id}`}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2.5 transition",
                            active ? "bg-slate-100" : "hover:bg-slate-50"
                          )}
                          onMouseEnter={() => setActiveIndex(idx)}
                          onClick={() => closeAll()}
                          role="option"
                          aria-selected={active}
                        >
                          <div className="h-12 w-12 rounded-xl border bg-white grid place-items-center overflow-hidden shrink-0"
                               style={{ borderColor: "rgb(var(--border))" }}>
                            {img ? (
                              <img src={img} alt={p.ad} className="h-full w-full object-cover" loading="lazy" />
                            ) : (
                              <span className="text-xs opacity-60 text-[rgb(var(--text-muted))]">Görsel</span>
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="truncate text-sm text-[rgb(var(--text))]">
                              {highlight(p.ad, trimmed)}
                            </div>
                            <div className="text-xs mt-0.5 text-[rgb(var(--text-muted))]">
                              {formatMoney(p.fiyat, p.paraBirimi)}
                            </div>
                          </div>

                          <span
                            className="text-xs font-semibold px-2 py-1 rounded-lg border text-[rgb(var(--text-muted))]"
                            style={{ borderColor: "rgb(var(--border))" }}
                          >
                            İncele
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>

                <button
                  type="button"
                  className="w-full text-sm px-3 py-2.5 border-t hover:bg-slate-50 transition flex items-center justify-center gap-2"
                  style={{ borderColor: "rgb(var(--border))", color: "rgb(var(--text))" }}
                  onClick={goResults}
                >
                  <ArrowUpDown size={14} className="opacity-70" />
                  <span className="truncate">“{trimmed}” için tüm sonuçları gör</span>
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchBox;
